/**
 * The organisation definition screens.
 *
 * Two chains meet on a location: operation → region says who answers for a store, and
 * country → city → district says where it stands. Everything here exists to let somebody
 * type them in, because nothing in the product may arrive as seed data in the code.
 */
import { request } from './client';

export interface Country {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  cityCount: number;
  locationCount: number;
}

export interface City {
  id: string;
  countryId: string;
  countryName: string;
  name: string;
  districtCount: number;
  locationCount: number;
}

export interface District {
  id: string;
  cityId: string;
  cityName: string;
  name: string;
  locationCount: number;
}

export interface Operation {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  regionCount: number;
}

export interface Region {
  id: string;
  operationId: string;
  operationName: string;
  code: string;
  name: string;
  isActive: boolean;
  locationCount: number;
}

export interface LocationType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  locationCount: number;
}

export interface Department {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;

  /** Head office rather than a store. Decides who an action may be handed to. */
  isHeadOffice: boolean;
}

export interface Location {
  id: string;
  code: string;
  name: string;
  locationTypeId: string | null;
  locationTypeName: string | null;
  regionId: string | null;
  regionName: string | null;
  operationName: string | null;
  countryId: string | null;
  countryName: string | null;
  cityId: string | null;
  cityName: string | null;
  districtId: string | null;
  districtName: string | null;
  address: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  areaSquareMetres: number | null;
  /** ISO date, no time. A shop opens on a day, not at an instant. */
  openedOn: string | null;
  description: string | null;
  isActive: boolean;
  /** Zero means it can still be deleted. Anything else and it is closed instead. */
  auditCount: number;
}

export type SaveCountry = Pick<Country, 'code' | 'name' | 'isActive'>;
export type SaveCity = { countryId: string; name: string };
export type SaveDistrict = { cityId: string; name: string };
export type SaveOperation = Pick<Operation, 'code' | 'name' | 'isActive'>;
export type SaveRegion = Pick<Region, 'operationId' | 'code' | 'name' | 'isActive'>;
export type SaveLocationType = Pick<LocationType, 'code' | 'name' | 'description' | 'isActive'>;
export type SaveDepartment = Pick<
  Department,
  'code' | 'name' | 'description' | 'isActive' | 'isHeadOffice'
>;
export type SaveLocation = Omit<
  Location,
  | 'id'
  | 'locationTypeName'
  | 'regionName'
  | 'operationName'
  | 'countryName'
  | 'cityName'
  | 'districtName'
  | 'auditCount'
>;

const base = '/api/v1/organisation';

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

const query = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const suffix = search.toString();
  return suffix ? `?${suffix}` : '';
};

/** The four calls every definition screen makes, for one path. */
function crud<TRow, TSave>(path: string) {
  return {
    list: (params: Record<string, string | undefined> = {}) =>
      request<TRow[]>(`${base}/${path}${query(params)}`),
    create: (body: TSave) => request<TRow>(`${base}/${path}`, json('POST', body)),
    update: (id: string, body: TSave) => request<TRow>(`${base}/${path}/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${base}/${path}/${id}`, { method: 'DELETE' }),
  };
}

export const organisation = {
  countries: crud<Country, SaveCountry>('countries'),
  cities: crud<City, SaveCity>('cities'),
  districts: crud<District, SaveDistrict>('districts'),
  operations: crud<Operation, SaveOperation>('operations'),
  regions: crud<Region, SaveRegion>('regions'),
  locationTypes: crud<LocationType, SaveLocationType>('location-types'),
  departments: crud<Department, SaveDepartment>('departments'),
  locations: {
    ...crud<Location, SaveLocation>('locations'),
    get: (id: string) => request<Location>(`${base}/locations/${id}`),
  },
};
